import React, { FC, useState } from 'react';
import axios from 'axios';

import { FormInput } from 'Shared/components';
import { TextInputProps } from 'Shared/components/FormInput/Variants';
import { useLazyV2Api } from 'Shared/services/Api';

import { GetVehicleByExactVinResponseType } from 'QuoteApp/components';
import { VehicleFieldsType } from 'QuoteApp/services/endpoints/GetDetails';
import { useAppContext } from 'QuoteApp/services/providers';
import { normalizeVin, VIN_REGEX } from 'QuoteApp/utils';
import { VehicleMultiselect } from 'QuoteApp/components/VehicleMultiselect';
import { ChangeMessage } from '../ChangeMessage';

type VinInputProps = Omit<TextInputProps, 'onChange'> & {
  vehicleFields: VehicleFieldsType;
  onChange(vehicleFields: VehicleFieldsType): void;
  onVinError: (vin: string) => void;
  onVinSuccess: () => void;
  onFieldError(message: string): void;
  vehicleId: string;
};

const vinErrorMessage =
  'This vehicle is not in our system. Please verify the VIN and try again.';

export const VinInput: FC<VinInputProps> = ({
  vehicleFields,
  onChange,
  onVinError,
  onVinSuccess,
  onFieldError,
  vehicleId,
  ...restOfProps
}) => {
  const { details } = useAppContext();
  const configDiscoveryParams = details.configDiscoveryParams;
  const title = `${vehicleFields.year} ${vehicleFields.make} ${vehicleFields.model}`;
  const [originalTitle] = useState<string>(title);
  const [{ loading }, executeVehicleByVin] = useLazyV2Api<{
    vehicles: GetVehicleByExactVinResponseType[];
  }>({
    url: '/vehicles/v2/vehicles',
  });

  const [multipleVehicleData, setMultipleVehicleData] = useState<
    GetVehicleByExactVinResponseType[]
  >([]);
  const [isEditingMultiple, setIsEditingMultiple] = useState<boolean>(false);

  const changeMessage =
    originalTitle &&
    originalTitle !== title &&
    vehicleFields.vehicleServicesId &&
    `Vehicle changed from "${originalTitle}" to "${title}" based on entered VIN`;

  return (
    <>
      <FormInput
        {...restOfProps}
        variant="text"
        onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
          onFieldError('');
          const newVin = normalizeVin(event.target.value);
          // Setting the user entered value now to update the UI, instead of waiting for a network call
          onChange({
            ...vehicleFields,
            vin: newVin,
            vehicleServicesId: '',
            partialVin: '',
          });
          if (VIN_REGEX.test(newVin)) {
            try {
              const { data } = await executeVehicleByVin({
                params: {
                  vin: newVin,
                  configDiscoveryParams,
                  exact_match: true,
                },
              });

              const vehicleLookup = data.vehicles || [];

              if (!vehicleLookup.length) {
                throw new Error('VIN not found');
              } else if (vehicleLookup.length === 1) {
                onVinSuccess();
                onChange({
                  ...vehicleFields,
                  vin: newVin,
                  year: vehicleLookup[0].year.toString(),
                  make: vehicleLookup[0].make,
                  model: vehicleLookup[0].model,
                  bodyStyle: vehicleLookup[0].bodyStyle,
                  vehicleServicesId: vehicleLookup[0].id,
                  partialVin: vehicleLookup[0].partialVin || '',
                });
              } else {
                // multiple vin radio selection
                setIsEditingMultiple(true);
                setMultipleVehicleData(vehicleLookup);
              }
            } catch (error) {
              if (axios.isCancel(error)) return;
              onFieldError(vinErrorMessage);
              onVinError(newVin);
            }
          }
        }}
        loading={loading}
      />
      {changeMessage && <ChangeMessage>{changeMessage}</ChangeMessage>}
      {isEditingMultiple && (
        <VehicleMultiselect
          vehicleId={vehicleId}
          data={multipleVehicleData}
          onChange={() => {
            onVinSuccess();
          }}
        />
      )}
    </>
  );
};
